// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// proeisincs, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xHeaderUpdate from "@eisinc/App/Handlers/XNG/Header";
import useSize from "@eisinc/App/Hooks/useSize";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { getIsCheckoutPage } from "@eisinc/App/Store/Selectors/CheckoutSelector";
import {
    getCartLinkWidget,
    getHeaderActionsWidget,
    getHeaderLinkListWidget,
    getHeaderSearchWidget,
    getHeaderSignInWidget,
    getHeaderSpotZone,
    getLogoImageWidget,
    getMainNavigationWidget,
    getQuickOrderWidget,
    getRfqWidget,
    getSalesRepWidget,
    getSecondaryNavigationWidget,
} from "@eisinc/App/Store/Selectors/GeneralSelectors";
import Close from "@eisinc/XDL/Icons/Close";
import Menu from "@eisinc/XDL/Icons/Menu";
import { xClassNames } from "@eisinc/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { Styles, StylesProvider } from "@insite/content-library/additionalStyles";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { breakpointMediaQueries, injectCss, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        logoImageWidget: getLogoImageWidget(state),
        mainNavigationWidget: getMainNavigationWidget(state),
        headerSignInWidget: getHeaderSignInWidget(state),
        cartLinkWidget: getCartLinkWidget(state),
        headerActionsWidget: getHeaderActionsWidget(state),
        secondaryNavigationWidget: getSecondaryNavigationWidget(state),
        headerLinkListWidget: getHeaderLinkListWidget(state),
        headerSearchWidget: getHeaderSearchWidget(state),
        isTabletScreen: state.xCommon.isTabletScreen,
        // XNG-Changes
        isCheckoutPage: getIsCheckoutPage(state),
        headerSpotZone: getHeaderSpotZone(state),
        quickOrderWidget: getQuickOrderWidget(state),
        rfqWidget: getRfqWidget(state),
        salesRepWidget: getSalesRepWidget(state),
    };
};

const mapDispatchToProps = {
    xHeaderUpdate,
};

type Props = PageProps &
    HasShellContext &
    ThemeProps<BaseTheme> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CompactHeaderStyles {
    page?: MobiusPageProps;
    navigationWrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    pageInnerWrapper?: InjectableCss;
    secondaryNavigationWrapper?: InjectableCss;
    // XNG-CHANGES
    wrapperStyledWrapper?: InjectableCss;
    logoLinksSearchWrapper?: InjectableCss;
    rightSectionWrapper?: InjectableCss;
    itemsWrapper?: InjectableCss;
    headerLinkListWrapper?: InjectableCss;
    mainNavigationPageWrapper?: MobiusPageProps;
    mainNavigationWrapper?: InjectableCss;
    closeMenuClickable?: ClickableProps;
    spotStyle?: InjectableCss;
}

const boxShadowStyleMap: Dictionary<string> = {
    light: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
    medium: "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
    dark: "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
};

function generateShadow(props: ThemeProps<BaseTheme>) {
    const { isDropShadow, dropShadowStyle } = props.theme.header;
    if (isDropShadow && dropShadowStyle in boxShadowStyleMap) {
        return `box-shadow: ${boxShadowStyleMap[dropShadowStyle]}`;
    }
    return "";
}

function generateBorder(props: ThemeProps<BaseTheme>) {
    const { isBorder, borderColor, borderThickness, isBorderTop, isBorderLeft, isBorderBottom, isBorderRight } =
        props.theme.header;
    if (!isBorder) {
        return "border: none;";
    }

    let border = `border: ${borderThickness}px solid ${resolveColor(borderColor, props.theme)};`;

    if (!isBorderTop) {
        border += "border-top: none;";
    }
    if (!isBorderBottom) {
        border += "border-bottom: none;";
    }
    if (!isBorderLeft) {
        border += "border-left: none;";
    }
    if (!isBorderRight) {
        border += "border-right: none;";
    }

    return border;
}

export const compactHeaderStyles: CompactHeaderStyles = {
    page: {
        padding: 0, // * Padding doesn't work with fullWidth because of logic on Line 64 if Page component
        fullWidth: [true, true, true, true, true], // make the header full width and control the margins with container css
        css: css`
            position: fixed;
            top: 0;
            left: 50%;
            z-index: 1;
            opacity: 0;
            transform: translateY(-100%) translateX(-50%);
            padding: ${props => props.theme.header.padding};
            ${generateBorder}
            ${generateShadow};
        `,
    },
    logoWrapper: {
        css: css`
            align-self: center;
        `,
    },
    searchWrapper: {
        css: css`
            align-self: center;
        `,
    },
    mainNavigationWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
};

const styles = compactHeaderStyles;

export const createStylesProviderStyles = (theme: BaseTheme) =>
    ({
        mainNavigation: {
            menuItemTypography: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        logo: {
            wrapper: {
                css: css`
                    width: 108px;
                    height: 40px;
                `,
            },
            img: {
                css: css`
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                `,
            },
        },
    }) as Styles;

const transitionStyles: Dictionary<{ opacity: number; transform: string }> = {
    entering: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    entered: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exiting: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exited: { opacity: 0, transform: "translateY(-100%) translateX(0)" },
};

// XNG-Change: style changes
const InnerWrapper = styled.div<{ isEdit?: boolean; isCheckoutPage?: boolean } & InjectableCss>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    ${(props: any) => {
        if (props.isEdit) {
            return "padding: 30px 10px;";
        }
        return "padding: 8px 16px;";
    }}
    width: 100%;
    justify-content: space-between;
    margin: 0 auto;
    ${({ theme, isEdit }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return isEdit
            ? css`
                  max-width: 100%;
              `
            : breakpointMediaQueries(
                  theme,
                  maxWidths.map((mw: any) =>
                      mw
                          ? css`
                                max-width: ${mw}px;
                            `
                          : null,
                  ),
              );
    }}
    & .logo-and-search-wrapper {
        display: flex;
        align-items: center;
        flex-grow: 1;
        gap: 54px;
    }
    ${injectCss}
`;

const CompactHeader = (props: Props) => {
    const { isCurrentPage, isEditing, isInShell } = props.shellContext;
    const isShell = isCurrentPage && isInShell;
    const isEdit = isCurrentPage && isEditing && isInShell;
    const [isShow, setIsShow] = useState(false);
    const transitionElement = React.useRef<HTMLElement | null>(null);

    // <XNG-Changes>
    const [showMainNavigation, setShowMainNavigation] = useState(false);
    // const headerWidth = typeof document !== "undefined" ? useElementSize(document.body)?.width : "100%";
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef);
    // </XNG-Changes>

    const headerWidth = useMemo(() => document.body.clientWidth, []);

    useEffect(() => {
        function eventHandler() {
            // XNG-Changes
            if (props.isCheckoutPage) {
                return;
            }

            const scrollPos = Math.round(window.scrollY);
            // XNG-Changes
            const triggerPoint = Number(props.theme.header.triggerPoint);

            const shouldShow = scrollPos > triggerPoint;

            if (shouldShow !== isShow) {
                setIsShow(shouldShow);
                props.xHeaderUpdate({ key: "isSticky", value: shouldShow });
            }
        }
        window.addEventListener("scroll", eventHandler, { passive: true });

        return () => {
            window.removeEventListener("scroll", eventHandler);
        };
    }, [props.theme.header.triggerPoint, isShow]);

    useEffect(() => {
        setIsShow(Boolean(isShell));
    }, [isShell]);

    // <XNG-Changes>
    useEffect(() => {
        if (headerHeight?.height) {
            props.xHeaderUpdate({ key: "stickyHeader", value: `${headerHeight.height}` });
        }
    }, [headerHeight?.height]);

    const openNavigationDrawer = () => {
        setShowMainNavigation(prevState => !prevState);
    };

    // XNG-Changes: Check if compact header is enabled in theme
    if (!props.theme.header.isCompactHeader) {
        return null;
    }

    if (isTabletScreen || props.isCheckoutPage) {
        return null;
    }
    // </XNG-Changes>

    // XNG-Changes: structure
    return (
        <Transition nodeRef={transitionElement} in={isShow} timeout={0}>
            {state => (
                <Page
                    ref={transitionElement}
                    {...styles.page}
                    as="header"
                    background={props?.theme?.header?.backgroundColor}
                    // XNG-Changes
                    data-test-selector="compact-header"
                    className={xClassNames.append(isShow ? "shown-compact-header" : "hidden-compact-header")}
                    style={{
                        transition: "opacity 200ms ease, transform 200ms ease",
                        pointerEvents: isShow ? "auto" : "none",
                        ...transitionStyles[state],
                        // XNG-Changes
                        maxWidth: headerWidth,
                    }}
                    {...{ showMainNavigation }}
                >
                    <StyledWrapper ref={headerRef}>
                        <StylesProvider styles={createStylesProviderStyles(props.theme)}>
                            <InnerWrapper {...styles.pageInnerWrapper} isEdit={isEdit}>
                                <StyledWrapper className="logo-and-search-wrapper" {...styles.logoLinksSearchWrapper}>
                                    <StyledWrapper {...styles.logoWrapper}>
                                        <Clickable
                                            onClick={openNavigationDrawer}
                                            data-test-selector="expandMobileMenu"
                                            aria-label="main-navigation"
                                            {...styles.closeMenuClickable}
                                        >
                                            <Icon
                                                src={showMainNavigation ? Close : Menu}
                                                size={showMainNavigation ? 14 : 24}
                                                color="text.link"
                                            />
                                        </Clickable>
                                        {createWidgetElement("Basic/Logo", {
                                            fields: {
                                                mobileSpecificImage: props.logoImageWidget?.logoImage,
                                                logoImage: props.logoImageWidget?.logoImage,
                                            },
                                        })}
                                    </StyledWrapper>

                                    <StyledWrapper {...styles.searchWrapper}>
                                        {createWidgetElement("Header/HeaderSearchInput", {
                                            fields: {
                                                ...props.headerSearchWidget,
                                            },
                                        })}
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper {...styles.rightSectionWrapper}>
                                    {props.quickOrderWidget && (
                                        <StyledWrapper {...styles.itemsWrapper}>
                                            {createWidgetElement(props.quickOrderWidget.type, {
                                                fields: props.quickOrderWidget.fields,
                                            })}
                                        </StyledWrapper>
                                    )}
                                    {props.rfqWidget && (
                                        <StyledWrapper {...styles.itemsWrapper}>
                                            {createWidgetElement(props.rfqWidget.type, {
                                                fields: props.rfqWidget.fields,
                                            })}
                                        </StyledWrapper>
                                    )}
                                    {props.salesRepWidget && (
                                        <StyledWrapper {...styles.itemsWrapper}>
                                            {createWidgetElement(props.salesRepWidget.type, {
                                                fields: props.salesRepWidget.fields,
                                            })}
                                        </StyledWrapper>
                                    )}
                                    <StyledWrapper {...styles.itemsWrapper}>
                                        {createWidgetElement("Common/HeaderSignIn", {
                                            fields: {
                                                ...props.headerSignInWidget,
                                            },
                                        })}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.itemsWrapper}>
                                        {createWidgetElement("Common/CartLink", {
                                            fields: {
                                                ...props.cartLinkWidget,
                                            },
                                        })}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </InnerWrapper>
                            <StyledWrapper {...{ showMainNavigation }} {...styles.navigationWrapper}>
                                <Page {...styles.mainNavigationPageWrapper}>
                                    <StyledWrapper {...styles.mainNavigationWrapper}>
                                        {createWidgetElement("Header/MainNavigation", {
                                            fields: { ...props.mainNavigationWidget },
                                        })}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.headerLinkListWrapper}>
                                        {createWidgetElement("Header/AddressLocations", {
                                            fields: {},
                                        })}
                                    </StyledWrapper>
                                </Page>
                            </StyledWrapper>
                        </StylesProvider>
                    </StyledWrapper>
                </Page>
            )}
        </Transition>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withIsInShell(CompactHeader))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
